import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as profileAPI from "../../lib/api/profileAPI";

/**
 * Async thunk for fetching user profile
 * Fetches from: users + user_profiles tables
 * Returns: { id, name, email, email_verified_at, username, avatar_url, created_at, updated_at }
 */
export const fetchUserProfile = createAsyncThunk(
  "profile/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.fetchUserProfile();
      return response;
    } catch (error) {
      const errorMessage = error?.message || error?.response?.data?.message || error?.data?.message || "Failed to fetch profile";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk for updating user profile
 * Updates: users + user_profiles tables
 */
export const updateUserProfile = createAsyncThunk(
  "profile/updateUserProfile",
  async (data, { rejectWithValue }) => {
    try {
      const response = await profileAPI.updateUserProfile(data);
      
      // Check if response is valid
      if (!response || typeof response !== 'object') {
        return rejectWithValue('Invalid response from server');
      }
      
      // Check if response has user data (successful update)
      if (response.id && response.email) {
        return response;
      } else if (response.error) {
        return rejectWithValue(response.error);
      } else {
        return rejectWithValue('Unexpected response format');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.error || error?.message || "Failed to update profile";
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  // User personal information
  // From users table: id, name, email, password_hash, email_verified_at, created_at, updated_at
  // From user_profiles table: user_id, username, avatar_url, bio, updated_at
  user: {
    id: null,
    name: "",
    email: "",
    email_verified_at: null,
    username: null, // from user_profiles
    avatar_url: null, // from user_profiles
    bio: null, // from user_profiles
    created_at: null,
    updated_at: null,
  },
  
  // Subscription details
  subscription: {
    tier: null, // free, basic, premium, pro
    frequency: null, // monthly, annual
    hasPaid: false,
    paidAt: null,
    expiresAt: null,
  },
  
  // Learning statistics
  stats: {
    totalLanguages: 0,
    sentencesCompleted: 0,
    hoursLearned: 0,
    currentStreak: 0,
    bestStreak: 0,
    progressByLanguage: {}, // { languageId: { completed, total, percentage } }
  },
  
  // User preferences & settings
  preferences: {
    theme: "light", // light, dark
    emailNotifications: true,
    pushNotifications: false,
    dailyReminder: true,
    reminderTime: "09:00",
    contentPreference: "mixed", // mixed, textual, audio, video
  },
  
  // Security information
  security: {
    twoFactorEnabled: false,
    emailVerified: false,
    passwordLastChanged: null,
    activeDevices: [],
    suspiciousActivities: [],
  },
  
  // Loading & error states
  loading: false,
  error: null,
  
  // UI state
  activeSection: "overview", // overview, account, subscription, learning, preferences, security, help
  editMode: false,
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    // Clear profile data (on logout)
    clearProfile: (state) => {
      return initialState;
    },
    
    setEditMode: (state, action) => {
      state.editMode = action.payload;
    },
    
    setActiveSection: (state, action) => {
      state.activeSection = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch user profile
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        // Safely destructure user data from subscription data
        if (action.payload && typeof action.payload === 'object') {
          const { subscription, ...userData } = action.payload;
          state.user = userData;
          if (subscription) {
            state.subscription = subscription;
          }
        } else {
          // If payload is not an object or is undefined, log error
          console.warn("Unexpected profile payload format:", action.payload);
          state.error = "Invalid profile response format";
        }
        state.loading = false;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
    
    // Update user profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload };
        state.loading = false;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const { clearProfile, setEditMode, setActiveSection } = profileSlice.actions;
export default profileSlice.reducer;